import { Autocomplete, IconButton, Skeleton, TextField, Tooltip, LinearProgress, Box, Button, ButtonGroup } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import ReactToPrint from '../../../Shared/print/ReactToPrint'
import { listShiftSummaryReportAPI } from './ReportsAPI/posShiftSummaryReportAPI'
import { useSelector } from 'react-redux'
import { convertDateFormat, today } from '../../../../Js/Date'
import { viewAllAllowedBranchesAPICall, viewAllBranchesAPICall } from '../../../../API/Settings/Company Settings/companySettingsAPI'
import "./posBillingReports.css"
import "./shiftSummaryPrint.css"
import { findSumOfObjkeys } from '../../../../Js/generalFunctions'
import Template from "./PosSummaryReportPrint"
import CleanTemplate from "./PosShiftSummaryPrintClean"
import ExcelJS from "exceljs"

const PosShiftSummaryReport = () => {
    const paymentsPrint = useRef();
const {tableLoading}=useSelector((state)=>state.mainDashBoardSlice)
const {reportsList}=useSelector((state)=>state.posShiftSummaryReportSlice)
const userInfo = useSelector((state) => state.loginResponseSlice.value);
const userRole=useSelector((state)=>state.userRoleSlice.value)

const allBranchesList = useSelector((state) => state.allBranchesSlice.value);
const allowedBranchList = useSelector((state) => state.allAllowedBranchesSlice.value);

const decimalPosition=localStorage.getItem("decimalPosition")
const userDefaultBranch={
    _id:userInfo?.branchPk,
    branchName:userInfo?.branchName
}
const formValuesInitial={
    branch:userRole==="user"?userDefaultBranch :null,
    fromDate:today,
    toDate:today
}
const [formValues,setFormValues]=useState(formValuesInitial)

const [grandTotals,setGrandTotals]=useState({})
const [printProgress, setPrintProgress] = useState({ loading: false, progress: 0, message: '' })
const [printMode, setPrintMode] = useState('fast') // 'fast' or 'pdf'
const handleInputChange=(key)=>(e,newValue)=>{
    setFormValues({...formValues,[key]:newValue===undefined?e.target.value:newValue})
}

const submitButtonClick=()=>{
    let body={
        branchId:formValues.branch?._id || null,
        startDate:formValues?.fromDate===""?undefined:`${formValues?.fromDate} 00:00:00`,
        endDate:formValues?.toDate===""?undefined:`${formValues?.toDate} 23:59:59`
    }
    listShiftSummaryReportAPI(body)
}

// Unified print function with mode selection
const handlePrint = async () => {
    if (!paymentsPrint.current) return;
    
    if (printMode === 'fast') {
        // Use fast direct print with new window approach
        setPrintProgress({ loading: true, progress: 30, message: 'Opening print window...' });
        
        // Wait for content to be fully rendered
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Debug: Check what content we have
        console.log('Print content children:', paymentsPrint.current.children.length);
        console.log('Print content height:', paymentsPrint.current.offsetHeight);
        console.log('Print content HTML preview:', paymentsPrint.current.innerHTML.substring(0, 500));
        
        // Force a reflow to ensure content is rendered
        paymentsPrint.current.style.display = 'block';
        paymentsPrint.current.style.visibility = 'visible';
        paymentsPrint.current.style.height = 'auto';
        
        // Wait for render
        await new Promise(resolve => setTimeout(resolve, 300));
        
        console.log('After force show - height:', paymentsPrint.current.offsetHeight);
        
        // Get the raw HTML content directly
        const rawHTML = paymentsPrint.current.innerHTML;
        
        console.log('Raw HTML length:', rawHTML.length);
        console.log('Raw HTML preview:', rawHTML.substring(0, 500));
        
        // Create a temporary div to modify the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = rawHTML;
        
        // Add print classes for proper section handling
        const tables = tempDiv.querySelectorAll('table');
        tables.forEach(table => {
            // Only prevent row breaks, let tables flow naturally
            table.classList.add('table-flow');
        });

        // Ensure header avoids page break after it
        const headerDiv = tempDiv.querySelector('.header-div');
        if (headerDiv) {
            headerDiv.classList.add('keep-with-next');
        }

        // Find and mark subtotals to stay at end of sections
        const subtotals = tempDiv.querySelectorAll('.grand-total, .subtotal, .section-total');
        subtotals.forEach(subtotal => {
            subtotal.classList.add('section-end');
        });

        // Find all shift section containers and mark them to avoid page breaks
        const shiftSections = tempDiv.querySelectorAll('.avoid-break');
        shiftSections.forEach(section => {
            // Find the shift info header and table within this section
            const shiftHeader = section.querySelector('.single-view-left-ctn');
            const shiftTable = section.querySelector('.new-global-table-container');
            
            if (shiftHeader && shiftTable) {
                // Mark both to keep together
                shiftHeader.classList.add('shift-section-header');
                shiftTable.classList.add('shift-section-table');
                
                // Add a wrapper class to the parent section
                section.classList.add('shift-section-wrapper');
            }
        });

        // Find the first top-level content block that contains a table
        let firstContentBlock = null;
        if (tempDiv && tempDiv.children && tempDiv.children.length) {
            for (let i = 0; i < tempDiv.children.length; i++) {
                const child = tempDiv.children[i];
                if (child !== headerDiv && child.querySelector && child.querySelector('table')) {
                    firstContentBlock = child;
                    break;
                }
            }
        }

        if (headerDiv && firstContentBlock) {
            // Check if they can be safely wrapped (must be siblings under tempDiv)
            const headerParent = headerDiv.parentNode;
            const blockParent = firstContentBlock.parentNode;

            if (headerParent === tempDiv && blockParent === tempDiv &&
                !headerDiv.contains(firstContentBlock) &&
                !firstContentBlock.contains(headerDiv)) {
                // Safe to wrap - they are siblings
                const wrapper = document.createElement('div');
                wrapper.className = 'header-content-wrapper';
                wrapper.style.cssText = 'page-break-inside: avoid; break-inside: avoid-page;';

                // Insert wrapper before header
                tempDiv.insertBefore(wrapper, headerDiv);

                // Move both elements into wrapper
                wrapper.appendChild(headerDiv);
                wrapper.appendChild(firstContentBlock);
            } else {
                // Fallback: enforce keep-with-next behavior via classes
                headerDiv.classList.add('header-content-wrapper');
                firstContentBlock.classList.add('header-content-wrapper');
            }
        }
        
        // Get the modified HTML
        const modifiedHTML = tempDiv.innerHTML;
        
        // Create print window
        const printWindow = window.open('', '_blank', 'width=1000,height=800');
        
        // Write the complete HTML with styles
        const printHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Shift Summary Report</title>
                <style>
                    @page { margin: 10mm; size: A4 portrait; }
                    body { 
                        font-family: Arial, sans-serif; 
                        margin: 0; 
                        padding: 15px; 
                        background: white;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    table { 
                        width: 100%; 
                        border-collapse: collapse; 
                        margin-bottom: 15px;
                    }
                    .table-flow {
                        /* Allow natural table flow, but prevent row breaks */
                    }
                    thead { 
                        display: table-header-group;
                    }
                    tbody { 
                        /* Allow natural flow */
                    }
                    th, td { 
                        border: 1px solid #ddd; 
                        padding: 8px; 
                        text-align: left; 
                        font-size: 12px;
                    }
                    tr { 
                        /* Prevent individual row breaks */
                        page-break-inside: avoid;
                    }
                    th { 
                        background-color: #f5f5f5; 
                        font-weight: bold; 
                    }
                    .grand-total { 
                        margin-top: 20px; 
                        border-top: 3px double #333; 
                        padding-top: 20px;
                        font-weight: bold;
                        page-break-inside: avoid;
                    }
                    .page-break { page-break-before: always; }
                    .print-header {
                        text-align: center;
                        margin-bottom: 20px;
                    }
                    .header-div {
                        margin-bottom: 20px;
                        border-bottom: 2px solid #333;
                        padding-bottom: 15px;
                    }
                    .header-content-wrapper {
                        /* Keep header + first content together */
                        page-break-inside: avoid;
                        break-inside: avoid-page;
                        page-break-after: auto;
                    }
                    .keep-with-next {
                        /* Prevent a page break immediately after header */
                        break-after: avoid-page;
                        page-break-after: avoid;
                    }
                    .section-end {
                        /* Keep subtotals at the end of their sections */
                        page-break-inside: avoid;
                        break-inside: avoid-page;
                        margin-top: 20px;
                        border-top: 2px solid #333;
                        padding-top: 15px;
                    }
                    .shift-section-wrapper {
                        /* Keep entire shift section together */
                        page-break-inside: avoid;
                        break-inside: avoid-page;
                    }
                    .shift-section-header {
                        /* Shift info header - keep with table */
                        page-break-after: avoid;
                        break-after: avoid-page;
                    }
                    .shift-section-table {
                        /* Shift table - keep with header */
                        page-break-before: avoid;
                        break-before: avoid-page;
                    }
                    .print-header h2 {
                        margin: 0;
                        font-size: 18px;
                        font-weight: bold;
                    }
                    .print-header p {
                        margin: 5px 0;
                        font-size: 14px;
                    }
                    /* Natural pagination - minimal restrictions */
                    @media print { 
                        .no-print { display: none !important; }
                        body { margin: 0; padding: 10mm; }
                        /* Only prevent row breaks, let everything else flow naturally */
                        tr { 
                            page-break-inside: avoid;
                        }
                        .grand-total {
                            page-break-inside: avoid;
                        }
                        /* Allow table headers to repeat on new pages */
                        thead { 
                            display: table-header-group;
                        }
                        /* Prevent tfoot from repeating - keep at section end only */
                        tfoot {
                            display: table-row-group;
                            page-break-inside: avoid;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="print-main-div">
                    ${modifiedHTML}
                </div>
                <script>
                    window.onload = function() {
                        setTimeout(() => {
                            console.log('Triggering print...');
                            window.print();
                        }, 1000);
                    };
                    window.onafterprint = function() {
                        setTimeout(() => {
                            window.close();
                        }, 500);
                    };
                </script>
            </body>
            </html>
        `;
        
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        setPrintProgress({ loading: false, progress: 100, message: 'Print window opened' });
        setTimeout(() => {
            setPrintProgress({ loading: false, progress: 0, message: '' });
        }, 2000);
        
    } else {
        // Use PDF mode (original method)
        setPrintProgress({ loading: true, progress: 0, message: 'Initializing PDF generation...' });
        
        try {
            const { printDomAsPdfWithPopup } = await import('../../../Shared/print/printUtils');
            
            const progressCallback = (progress, message) => {
                setPrintProgress({ loading: true, progress, message });
            };
            
            await printDomAsPdfWithPopup(paymentsPrint.current, {
                scale: 2,
                background: '#ffffff',
                orientation: 'portrait',
                onProgress: progressCallback
            });
            
            setPrintProgress({ loading: false, progress: 100, message: 'PDF ready!' });
            setTimeout(() => {
                setPrintProgress({ loading: false, progress: 0, message: '' });
            }, 2000);
            
        } catch (error) {
            console.error('PDF generation failed:', error);
            setPrintProgress({ loading: false, progress: 0, message: 'PDF generation failed' });
            setTimeout(() => {
                setPrintProgress({ loading: false, progress: 0, message: '' });
            }, 3000);
        }
    }
};

// Alternative: Super fast new window print
const handleFastPrint = () => {
    if (!paymentsPrint.current) return;
    
    setPrintProgress({ loading: true, progress: 30, message: 'Opening print window...' });
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Shift Summary Report</title>
            <style>
                @page { margin: 10mm; size: A4; }
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f5f5f5; font-weight: bold; }
                .grand-total { margin-top: 20px; border-top: 3px double #333; padding-top: 20px; }
                .page-break { page-break-before: always; }
                .avoid-break { page-break-inside: avoid; }
                @media print { .no-print { display: none; } }
            </style>
        </head>
        <body>
            ${paymentsPrint.current.innerHTML}
            <script>
                window.onload = function() {
                    setTimeout(() => window.print(), 500);
                }
            </script>
        </body>
        </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setPrintProgress({ loading: false, progress: 100, message: 'Print window opened' });
    setTimeout(() => {
        setPrintProgress({ loading: false, progress: 0, message: '' });
    }, 2000);
};
const handleDownload = () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Shift Summary Report');
    sheet.properties.defaultRowHeight = 20;

    // Add header section
    const titleRow = sheet.addRow(['Shift Summary Report']);
    titleRow.font = { size: 18, bold: true };
    sheet.mergeCells('A1:G1');
    titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };

    sheet.addRow(['']);

    reportsList?.forEach((list) => {
        // Add shift details
          // Add shift details
          const shiftIdRow = sheet.addRow([`${list?.shift?.shiftId || "-NIL-"}`]);
          shiftIdRow.font = { size: 15, bold: true };
          sheet.mergeCells(`A${sheet.lastRow._number}:G${sheet.lastRow._number}`);
          const shiftIdRowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F8F8' } };
          shiftIdRow.eachCell((cell) => {
              cell.fill = shiftIdRowFill;
          });
  
          const startByRow = sheet.addRow([`Start by: ${list?.shift?.startedBy || ''}`, '', '', `End by: ${list?.shift?.endedBy || ''}`]);
          startByRow.font = { size: 12, bold: false };
          startByRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
          startByRow.getCell(4).alignment = { vertical: 'middle', horizontal: 'left' };
          sheet.mergeCells(`A${sheet.lastRow._number}:C${sheet.lastRow._number}`);
          sheet.mergeCells(`D${sheet.lastRow._number}:G${sheet.lastRow._number}`);
          const startByRowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F8F8' } };
          startByRow.eachCell((cell) => {
              cell.fill = startByRowFill;
          });
  
          const startDateRow = sheet.addRow([`Start Date: ${list?.shift?.startDate || ''}`, '', '', `End Date: ${list?.shift?.endDate || ''}`]);
          startDateRow.font = { size: 12, bold: false };
          startDateRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
          startDateRow.getCell(4).alignment = { vertical: 'middle', horizontal: 'left' };
          sheet.mergeCells(`A${sheet.lastRow._number}:C${sheet.lastRow._number}`);
          sheet.mergeCells(`D${sheet.lastRow._number}:G${sheet.lastRow._number}`);
          const startDateRowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F8F8' } };
          startDateRow.eachCell((cell) => {
              cell.fill = startDateRowFill;
          });

        // Add a blank row for separation
        sheet.addRow([]);

        // Add table headers
        const headerRow = sheet.addRow(['Order Date', 'Order Ref', 'Customer', 'Mobile', 'Order Amount', "Paid Amount", 'Credit']);
        headerRow.font = { size: 12, bold: true };
        const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFedf2f9' } };
        headerRow.eachCell((cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.fill = headerFill;
        });

        sheet.columns = [
            { header: "Order Date", key: "orderDate", width: 25 },
            { header: "Order Ref", key: "name", width: 25 },
            { header: "Customer", key: "customerName", width: 25 },
            { header: "Mobile", key: "custMobile", width: 25 },
            { header: "Order Amount", key: "amtTotal", width: 25 },
            { header: "Paid Amount", key: "paidAmt", width: 25 },
            { header: "Shift Summary Report", key: "amountDue", width: 25 },
        ];

        // Add table data
        list?.orders?.forEach((order, index) => {
            const row = sheet.addRow({
                orderDate: convertDateFormat(order?.orderDate),
                name: order?.name,
                customerName: order?.customerName,
                custMobile: order?.custMobile,
                amtTotal: order?.amtTotal?.toFixed(decimalPosition),
                paidAmt: order?.paidAmt?.toFixed(decimalPosition),
                amountDue: order?.amountDue?.toFixed(decimalPosition)
            });

            if (index % 2 === 1) {
                const alternateRowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F8F8' } };
                row.eachCell((cell) => {
                    cell.fill = alternateRowFill;
                });
            }

            row.eachCell((cell, colNumber) => {
                if (colNumber >= 4) {
                    cell.alignment = { horizontal: "right" };
                } else {
                    cell.alignment = { horizontal: "center" };
                }
            });
        });

        const totalRow = sheet.addRow([
            '',
            '',
            '',
            'TOTAL',
            list?.totalAmtTotal?.toFixed(decimalPosition),
            list?.totalPaidAmt?.toFixed(decimalPosition),
            list?.totalDueAmount?.toFixed(decimalPosition)
        ]);
        totalRow.font = { size: 12, bold: true };

        const totalRowFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDFD9D9' } };
        totalRow.eachCell((cell) => {
            cell.fill = totalRowFill;
            cell.alignment = { vertical: 'middle', horizontal: 'right' };
        });

        // Add a blank row for separation
        sheet.addRow([]);
    });

    // Add grand total
    // const grandTotalRow = sheet.addRow([
    //     '',
    //     '',
    //     '',
    //     'GRAND TOTAL',
    //     Number(grandTotals?.totalAmtTotal)?.toFixed(decimalPosition),
    //     Number(grandTotals?.totalPaidAmt)?.toFixed(decimalPosition),
    //     Number(grandTotals?.totalDueAmount)?.toFixed(decimalPosition)
    // ]);
    // grandTotalRow.font = { size: 12, bold: true };
    // const grandTotalFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFb7b6b6' } };
    // grandTotalRow.eachCell((cell) => {
    //     cell.alignment = { vertical: 'middle', horizontal: 'right' };
    //     cell.fill = grandTotalFill;
    // });

    workbook.xlsx.writeBuffer().then((data) => {
        const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'CashCardSummary.xlsx';
        anchor.click();
        window.URL.revokeObjectURL(url);
    });
};

// initial api calls
useEffect(()=>{
    viewAllAllowedBranchesAPICall({employeeId: userInfo?.data?._id }) 
    submitButtonClick()
},[])

useEffect(()=>{
if(reportsList.length!==0){
    let sum=findSumOfObjkeys(reportsList,"totalAmtTotal","totalPaidAmt","totalDueAmount")
    setGrandTotals(sum)
}else{
    setGrandTotals({})
}
},[reportsList])

// Debug ref after component mounts
useEffect(() => {
    console.log('Component mounted, ref:', paymentsPrint.current);
    if (paymentsPrint.current) {
        console.log('Ref element children count:', paymentsPrint.current.children.length);
        console.log('Ref element height:', paymentsPrint.current.offsetHeight);
    }
}, []);

    return (
        <div className="global-page-parent-container" style={{position:"relative"}}>
            <div className="global-white-bg-container Header" style={{ display: "flex", justifyContent: "space-between", padding: "0px 30px 0 25px", alignItems: "center" }}>
                <p>Shift Summary Report</p>
                <div>
                    <ButtonGroup size="small" sx={{ mr: 1 }}>
                        <Button
                            variant={printMode === 'fast' ? 'contained' : 'outlined'}
                            onClick={() => setPrintMode('fast')}
                            sx={{ 
                                fontSize: '11px', 
                                py: 0.5, 
                                px: 1,
                                backgroundColor: printMode === 'fast' ? '#d787e7' : 'transparent',
                                color: printMode === 'fast' ? 'white' : '#d787e7',
                                borderColor: '#d787e7',
                                '&:hover': {
                                    backgroundColor: printMode === 'fast' ? '#c970d8' : 'rgba(215, 135, 231, 0.04)'
                                }
                            }}
                        >
                            ⚡ Fast
                        </Button>
                        <Button
                            variant={printMode === 'pdf' ? 'contained' : 'outlined'}
                            onClick={() => setPrintMode('pdf')}
                            sx={{ 
                                fontSize: '11px', 
                                py: 0.5, 
                                px: 1,
                                backgroundColor: printMode === 'pdf' ? '#d787e7' : 'transparent',
                                color: printMode === 'pdf' ? 'white' : '#d787e7',
                                borderColor: '#d787e7',
                                '&:hover': {
                                    backgroundColor: printMode === 'pdf' ? '#c970d8' : 'rgba(215, 135, 231, 0.04)'
                                }
                            }}
                        >
                            📄 PDF
                        </Button>
                    </ButtonGroup>
                    
                    <Tooltip title={printProgress.loading ? printProgress.message : "Print"}>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <IconButton 
                                className="printer-div" 
                                style={{ padding: "1px" }}
                                onClick={handlePrint}
                                disabled={printProgress.loading}
                            >
                                <i 
                                    style={{ 
                                        color: printProgress.loading ? "#ccc" : "white", 
                                        backgroundColor: printProgress.loading ? "#999" : "#d787e7", 
                                        fontSize: "medium", 
                                        padding: "8px", 
                                        borderRadius: "2px" 
                                    }} 
                                    class="bi bi-printer"
                                ></i>
                            </IconButton>
                            
                            {printProgress.loading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -2,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        borderRadius: '2px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <LinearProgress
                                        variant="determinate"
                                        value={printProgress.progress}
                                        sx={{
                                            height: '100%',
                                            backgroundColor: 'transparent',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: '#d787e7',
                                                transition: 'none'
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                            
                            {printProgress.loading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -20,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: '#333',
                                        color: 'white',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap',
                                        zIndex: 1000
                                    }}
                                >
                                    {Math.round(printProgress.progress)}%
                                </Box>
                            )}
                        </Box>
                    </Tooltip>

                    <Tooltip title="Download">
                        <IconButton onClick={handleDownload}>
                            <i class="bi bi-arrow-down-circle" style={{ backgroundColor: "rgb(255 118 133 / 69%)", color: "white", fontSize: "medium", padding: "8px", borderRadius: "2px" }}></i>
                        </IconButton>
                    </Tooltip>
                </div>

            </div>

            <div className="global-white-bg-container " style={{ display: "flex", alignItems: "center" }}>
                <div className="new-global-single-input auto-complete-new">
                    <Autocomplete
                        options={userRole === "admin" ? allBranchesList : allowedBranchList || []}
                        getOptionLabel={(option) => option?.branchName || ''}
                        isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                        renderInput={(params) => (
                            <TextField {...params} label="Branch" focused variant='outlined' />
                        )}
                        value={formValues?.branch}
                        onChange={handleInputChange("branch")}
                    />
                </div>


                <div className="new-global-single-input" style={{ width: '21%' }}>
                    <TextField
                        id="outlined-basic"
                        placeholder="From"
                        onChange={handleInputChange("fromDate")}
                        value={formValues?.fromDate}
                        variant="outlined"
                        type="date"
                        focused
                        label="From Date"
                    />
                </div>



                <div className="new-global-single-input" style={{ width: '21%' }}>
                    <TextField
                        id="outlined-basic"
                        placeholder="From"
                        onChange={handleInputChange("toDate")}
                        value={formValues?.toDate}
                        variant="outlined"
                        type="date"
                        focused
                        label="To Date"
                    />
                </div>

                <button
                onClick={submitButtonClick}
                className='btn btn-primary'>Submit</button>

            </div>

            <div className='global-white-bg-container shift-report-table-container'>

                {
                
                 tableLoading?
                 <div className="new-global-table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Order Date</th>
                                <th>Order Ref</th>
                                <th>Customer</th>
                                <th>Mobile</th>
                                <th>Order Amount</th>
                                <th>Paid Amount</th>
                                <th>Bank</th>
                                <th>Cash</th>
                                <th>Credit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                            [...Array(10)].map((_,i)=>(
                            <tr key={i}>
                                <td colspan={15}>
                                    <Skeleton variant="rectangular"
                                    animation="wave"
                                    width={"100%"}
                                    height={40} />
                                </td>
                            </tr>
                            ))    
                        }
                        </tbody>
                    </table>
                 </div>
                 :
                 reportsList?.length!==0?
                 reportsList?.map((list,i)=>(
                     <>
                         <div className="single-view-left-ctn" style={{ width: "60%", minWidth: "fit-content", maxWidth: "550px", padding: "16px", margin: "1%", marginBottom: "0", backgroundColor: "rgb(248 248 248)" }}>
                             <h3 style={{ margin: "0", cursor: "pointer", fontSize: "larger" }}>{list?.shift?.shiftId || "-NIL-"}</h3>
                             <div className="justify-space-between" style={{ display: "flex" }}>
                                 <div style={{ display: "flex", gap: "10px" }}>
                                     <div className="single-view-info-ctn-left flex-column">
                                         <p style={{ margin: "1px" }}>Start by</p>
                                         <p style={{ margin: "1px" }}>Start Date</p>
                                     </div>
                                     <div className="single-view-info-ctn-right flex-column">
                                         <p style={{ margin: "1px" }}> {list?.shift?.startedBy}</p>
                                         <p style={{ margin: "1px" }}>{list?.shift?.startDate}</p>
                                     </div>
                                 </div>
                                 <div style={{ display: "flex", gap: "10px" }}>
                                     <div className="single-view-info-ctn-left flex-column">
                                         <p style={{ margin: "1px" }}>End by</p>
                                         <p style={{ margin: "1px" }}>End Date</p>
                                     </div>
                                     <div className="single-view-info-ctn-right flex-column">
                                         <p style={{ margin: "1px" }}>{list?.shift?.endedBy}</p>
                                         <p style={{ margin: "1px" }}>{list?.shift?.endDate}</p>
                                     </div>
                                 </div>
                             </div>
                         </div>

                         <div className="new-global-table-container" style={{padding:"8px 8px 19px 8px"}} >
                             <table>
                                 <thead >
                                     <tr>
                                         <th>Order Date</th>
                                         <th>Order Reff</th>
                                         <th>Customer</th>
                                         <th>Mobile</th>
                                         <th>Order Amount</th>
                                         <th>Paid Amount</th>
                                         <th>Credit</th>

                                     </tr>
                                 </thead>
                                 <tbody>
                                     {list?.orders?.length!==0?
                                     list?.orders?.map((item, i) => (
                                         <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "white" : "#e9e7e7" }}>
                                             <td style={{ border: "none", height: "29px" }}>{convertDateFormat(item?.orderDate)}</td>
                                             <td style={{ border: "none", height: "29px" }}>{item?.name}</td>
                                             <td style={{ border: "none", height: "29px" }}>{item?.customerName}</td>
                                             <td style={{ border: "none", height: "29px" }}>{item?.custMobile}</td>
                                             <td style={{ border: "none", height: "29px" }}>{item?.amtTotal?.toFixed(decimalPosition)}</td>
                                             <td style={{ border: "none", height: "29px" }}>{item?.paidAmt?.toFixed(decimalPosition)}</td>
                                             <td style={{ border: "none", height: "29px" }}>{item?.amountDue?.toFixed(decimalPosition)}</td>
                                         </tr>
                                     ))
                                    :
                                    <tr>
                                        <td colspan={7}>No Records found</td>
                                    </tr>
                                    }
                                 </tbody>
                                 <tfoot>
                                     <tr style={{ backgroundColor: "#dfd9d9" }}>
                                         <td colSpan={4} style={{ textAlignLast: "end" }}>TOTAL</td>
                                         <td style={{ textAlignLast: "center" }}>{list?.totalAmtTotal?.toFixed(decimalPosition)}</td>
                                         <td style={{ textAlignLast: "center" }}>{list?.totalPaidAmt?.toFixed(decimalPosition)}</td>
                                         <td style={{ textAlignLast: "center" }}>{list?.totalDueAmount?.toFixed(decimalPosition)}</td>
                                     </tr>
                                 </tfoot>
                             </table>
                         </div>
                     </>
                 ))   
                : 
                <div className="center-div">
                    No Records
                </div>
                }
            </div>

            {
            Object.keys(grandTotals).length!==0 &&    
            <div className="new-global-table-container report-totalbar" >
                <table>
                    <thead style={{display:"none"}}>
                        <tr>
                            <th>Order Date</th>
                            <th>Order Reff</th>
                            <th>Customer</th>
                            <th>Mobile</th>
                            <th>Order Amount</th>
                            <th>Paid Amount</th>
                            <th>Credit</th>

                        </tr>
                    </thead>
                    {/* <tfoot>
                        <tr>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>&nbsp;</td>
                            <td>GRAND TOTAL</td>
                            <td>{Number(grandTotals?.totalAmtTotal)?.toFixed(decimalPosition)}</td>
                            <td>{Number(grandTotals?.totalPaidAmt)?.toFixed(decimalPosition)}</td>
                            <td>{Number(grandTotals?.totalDueAmount)?.toFixed(decimalPosition)}</td>
                        </tr>
                    </tfoot> */}
                </table>
            </div>}
            <div style={{position: 'absolute', left: '-9999px', top: '0', width: '210mm', height: 'auto'}}>
  <div ref={paymentsPrint} style={{height: 'auto', maxHeight: 'none', overflow: 'visible'}}>
    <Template formValues={formValues} grandTotals={grandTotals}/>
  </div>
</div>
        </div>
    )
}

export default PosShiftSummaryReport